/**
 * Announces that a campaign has an unpublished draft and hosts the actions for it.
 *
 * Exists because a pending draft is a fact about the campaign, not a view preference:
 * before this, a draft was only discoverable by toggling a "Show Draft" switch, so a
 * user could miss one entirely. Owning both the diff toggle and the publish action
 * keeps every draft affordance in one place.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { DateTime } from 'luxon';

import { useTranslation } from 'src/arb-wrapper';

/**
 * Relative "time ago" for the draft freshness line.
 *
 * Luxon (already a dependency, used e.g. in AssetCard) handles the unit selection,
 * pluralisation, and — unlike a hand-rolled delta — future timestamps, which occur
 * with clock skew and would otherwise render as "0s ago".
 */
function formatRelativeTime (isoTimestamp: string | undefined): string | undefined {
  if (!isoTimestamp) return undefined;
  const parsed = DateTime.fromISO(isoTimestamp);
  return parsed.isValid ? parsed.toRelative() ?? undefined : undefined;
}

interface DraftPendingBannerProps {
  viewDraft: boolean;
  canPublish: boolean;
  isPublishing: boolean;
  updatedAt: string | undefined;
  onToggleDraft: () => void;
  onPublish: () => void;
}

/**
 * Announces that an unpublished draft exists and hosts the publish action.
 *
 * Exists because a pending draft is a fact about the campaign, not a view
 * preference: before this, the draft was only discoverable by toggling "Show
 * Draft", so a user could miss it entirely. Surfacing who last edited the draft
 * and when also lets the user judge freshness before publishing, since the
 * agent can edit a draft concurrently.
 */
export const DraftPendingBanner: React.FC<DraftPendingBannerProps> = ({
  viewDraft, canPublish, isPublishing, updatedAt, onToggleDraft, onPublish,
}) => {
  const { t } = useTranslation();
  const relativeTime = formatRelativeTime(updatedAt);

  return (
    <Box spacingInset="400" type="fill">
      <Row alignmentVertical="center" spacing="300" widths={['fill', 'fit']}>
        <Column spacing="100">
          <Text type="h100">{t('publisher_campaign_config_draft_pending')}</Text>
          {relativeTime && (
            <Text color="secondary" type="b200">
              {t('publisher_campaign_config_draft_last_updated', { time: relativeTime })}
            </Text>
          )}
        </Column>
        <Row alignmentVertical="center" spacing="300">
          <Button
            aria-pressed={viewDraft}
            data-testid="toggle-draft-changes-button"
            size="small"
            type="tertiary"
            onClick={onToggleDraft}
          >
            {viewDraft
              ? t('publisher_campaign_config_hide_changes')
              : t('publisher_campaign_config_review_draft')}
          </Button>
          {canPublish && (
            <Button
              data-testid="publish-campaign-draft-button"
              disabled={isPublishing}
              size="small"
              type="primary"
              onClick={onPublish}
            >
              {t('publisher_campaign_config_publish')}
            </Button>
          )}
        </Row>
      </Row>
    </Box>
  );
};
